import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './CallInfo.css';

const CallInfo = () => {
  const [emiDues, setEmiDues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCallInfo();
  }, []);

  const fetchCallInfo = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/call-info`);
      if (response.ok) {
        const data = await response.json();
        setEmiDues(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch call info');
      }
    } catch (err) {
      console.error('Error fetching call info:', err);
      setError(err.message || 'Failed to load EMI dues information');
    } finally {
      setIsLoading(false);
    }
  };

  const getRowClassName = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'row-past'; // Red for past dates
    } else if (diffDays === 0) {
      return 'row-today'; // Orange for today
    } else if (diffDays <= 2) {
      return 'row-upcoming'; // Green for next 2 days
    }
    return '';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="call-info-container">
      <h2>Call Info - EMI Due Report</h2>
      
      <div className="legend-container">
        <div className="legend-item">
          <span className="legend-color red"></span>
          <span>Past Due Dates</span>
        </div>
        <div className="legend-item">
          <span className="legend-color orange"></span>
          <span>Today</span>
        </div>
        <div className="legend-item">
          <span className="legend-color green"></span>
          <span>Next 2 Days</span>
        </div>
      </div>

      {isLoading && (
        <div className="loading-message">Loading EMI dues information...</div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      {!isLoading && !error && (
        <div className="table-container">
          <table className="call-info-table">
            <thead>
              <tr>
                <th>Account No</th>
                <th>Name</th>
                <th>Loan Type</th>
                <th>Debtor Phone No</th>
                <th>Due Date</th>
                <th>Due Amount</th>
              </tr>
            </thead>
            <tbody>
              {emiDues.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data-message">
                    No EMI dues found for the specified date range.
                  </td>
                </tr>
              ) : (
                emiDues.map((item, index) => (
                  <tr key={index} className={getRowClassName(item.due_date)}>
                    <td>{item.account_no}</td>
                    <td>{item.name}</td>
                    <td>
                      <span className={`loan-type-badge ${item.loan_type === 'GL Loan' ? 'badge-gl' : item.loan_type === 'CD Loan' ? 'badge-cd' : ''}`}>
                        {item.loan_type || 'N/A'}
                      </span>
                    </td>
                    <td>{item.phone}</td>
                    <td>{formatDate(item.due_date)}</td>
                    <td className="amount-cell">{formatCurrency(item.due_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="refresh-button-container">
        <button onClick={fetchCallInfo} className="refresh-button" disabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
};

export default CallInfo;

